import { RuntimeConfig } from '../types';

export interface MockProvisionClientConfig {
  endpoint: string;
  authToken: string;
  idempotencyKey?: string;
  requestId?: string;
  allowProductionEndpoint?: boolean; // For dev escape hatch, default false
}

export interface MockProvisionSuccessResponse {
  ok: true;
  mode: string;
  storage: string;
  memoryOnly: boolean;
  provisioned: boolean;
  mockDormId?: string;
  idempotentReplay?: boolean;
  adminConfigPreview?: any;
  tenantConfigPreview?: any;
  safeSummary?: any;
  warnings?: string[];
  [key: string]: any;
}

export interface MockProvisionErrorResponse {
  ok: false;
  errorCode: string;
  errors?: any[];
  warnings?: string[];
  safeToRetry?: boolean;
}

export type MockProvisionResponse = MockProvisionSuccessResponse | MockProvisionErrorResponse;

/**
 * Validates the mock provision client configuration to ensure safety boundaries.
 */
export function validateMockProvisionClientConfig(config: MockProvisionClientConfig): string | null {
  if (!config.endpoint || config.endpoint.trim() === '') {
    return 'MISSING_ENDPOINT';
  }
  if (!config.authToken || config.authToken.trim() === '') {
    return 'MISSING_AUTH_TOKEN';
  }
  
  const isProductionLooking = config.endpoint.includes('workers.dev') || 
                              config.endpoint.includes('apartment-production') || 
                              config.endpoint.includes('horplus');
                              
  if (isProductionLooking && !config.allowProductionEndpoint) {
    return 'FORBIDDEN_PRODUCTION_ENDPOINT';
  }

  return null;
}

/**
 * Sanitizes the payload to ensure no sensitive dev data is accidentally sent
 * Note: The buildApartmentRuntimeConfig already does a lot of this, but this is a secondary guard.
 */
export function buildMockProvisionRequestPayload(runtimeConfig: RuntimeConfig): any {
  // Deep clone to avoid mutating the original
  const sanitized = JSON.parse(JSON.stringify(runtimeConfig));
  
  // Ensure we don't accidentally send sensitive keys if they were somehow injected
  // (In a real app, this would strip 'token', 'secret', etc., but we know RuntimeConfig structure)
  // For safety boundary, we inject a source marker
  return {
    ...sanitized,
    _sourceMarker: 'dormitory-setup-wizard-ui'
  };
}

/**
 * Calls the HorPlus Worker Mock Provision endpoint.
 * This function is UI-agnostic and safe to use. It does not touch localStorage or persist data.
 * It will catch network errors and return them as safe ErrorResponses.
 */
export async function callMockProvisionEndpoint(
  config: MockProvisionClientConfig, 
  runtimeConfigPayload: RuntimeConfig,
  fetcher: typeof fetch = fetch // Allow dependency injection for testing
): Promise<MockProvisionResponse> {
  
  const validationError = validateMockProvisionClientConfig(config);
  if (validationError) {
    return {
      ok: false,
      errorCode: validationError,
      safeToRetry: false
    };
  }

  const payload = buildMockProvisionRequestPayload(runtimeConfigPayload);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${config.authToken}`
  };

  if (config.idempotencyKey) {
    headers['Idempotency-Key'] = config.idempotencyKey;
  }
  if (config.requestId) {
    headers['X-Request-Id'] = config.requestId;
  }

  try {
    const response = await fetcher(config.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    // Handle generic HTTP errors before parsing JSON
    if (response.status === 401) {
      return { ok: false, errorCode: 'UNAUTHORIZED_OR_MISSING_TOKEN', safeToRetry: false };
    }
    if (response.status === 403) {
      return { ok: false, errorCode: 'FORBIDDEN_STORAGE_MODE_OR_BLOCKED', safeToRetry: false };
    }

    const data = await response.json();

    if (response.ok) {
      return {
        ok: true,
        mode: data.mode,
        storage: data.storage,
        memoryOnly: data.memoryOnly,
        provisioned: data.provisioned,
        mockDormId: data.mockDormId,
        idempotentReplay: data.idempotentReplay,
        adminConfigPreview: data.adminConfigPreview,
        tenantConfigPreview: data.tenantConfigPreview,
        safeSummary: data.safeSummary,
        warnings: data.warnings
      } as MockProvisionSuccessResponse;
    } else {
      // 400, 409, 422, etc.
      return {
        ok: false,
        errorCode: data.errorCode || 'UNKNOWN_API_ERROR',
        errors: data.errors,
        warnings: data.warnings,
        safeToRetry: response.status !== 409 // 409 is conflict, shouldn't blindly retry same payload
      };
    }
  } catch (error: any) {
    // Network errors, DNS resolution failures, CORS failures, JSON parse errors
    return {
      ok: false,
      errorCode: 'NETWORK_OR_PARSE_ERROR',
      warnings: ['Failed to connect to the mock endpoint. Ensure the Worker is running locally and CORS is configured.'],
      safeToRetry: true
    };
  }
}
